import React from 'react';
import columns from './columns';
import { PreviewWarningLabel as WarningLabel } from '../../styles';
import PreviewStepBase from '../../base/PreviewStepBase';
import { FallbackFieldConfig, useCsvPreviewDataSource } from '../../base/useCsvPreviewDataSource';

// Define the mandatory Reference fields that should be filled with "Unknown" if the CSV cell is empty
const refFieldKeys = new Set(['asset_type', 'manufacturer', 'model']);
const refFieldFallback: FallbackFieldConfig = { fields: refFieldKeys, placeholder: 'Unknown' };

// Location hierarchy fields that have no Unknown fallback — empty cells will cause import failures
const locationFieldKeys = [
  'location_country',
  'location_state',
  'location_city',
  'location_building',
];

export interface PreviewStepProps {
  mapping: Record<string, string>;
  csvTopLines: string[][];
}

const PreviewStep: React.FC<PreviewStepProps> = ({ mapping, csvTopLines }) => {
  const columnsFields = Object.values(columns).map((col) => col.dataField);

  const dataSource = useCsvPreviewDataSource(
    mapping,
    csvTopLines,
    columnsFields,
    { field: 'status', value: 'Installed' },
    undefined,
    refFieldFallback,
  );

  const hasEmptyLocationCell = React.useMemo(
    () =>
      dataSource.some((row) => locationFieldKeys.some((field) => mapping[field] && !row[field])),
    [dataSource, mapping],
  );

  return (
    <PreviewStepBase
      infoText={
        <>
          Here is a preview of how your new assets will look like in the Assets Table. You can go
          back and edit your columns if needed before submitting.
        </>
      }
      warningNode={
        hasEmptyLocationCell && (
          <WarningLabel>
            One or more rows in the preview have empty location values (Country, State, City or
            Building). Rows with missing location data will fail to import — go back and ensure
            every row in your CSV file has values for all location columns.
          </WarningLabel>
        )
      }
      dataGridId="data-import-preview"
      pluralText="Assets"
      singularText="Asset"
      columns={columns}
      dataSource={dataSource}
    />
  );
};

export default React.memo(PreviewStep);
